import styled from 'styled-components';
import { format } from 'date-fns';

type FXReceiptCancelModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  foreignCurrencyData?: {
    createdDate: string;
    amount: number;
  };
};

function FXReceiptCancelModal({
  isOpen,
  onClose,
  onConfirm,
  foreignCurrencyData,
}: FXReceiptCancelModalProps) {
  if (!isOpen || !foreignCurrencyData) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Title>현금 영수증 발행 취소</Title>
        <Subtitle>현금 영수증 발행을 취소하시겠습니까?</Subtitle>

        <InfoSection>
          <InfoRow>
            <InfoLabel>발행 일자</InfoLabel>
            <InfoValue>
              {format(
                new Date(foreignCurrencyData.createdDate),
                'yyyy-MM-dd HH:mm',
              )}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>금액</InfoLabel>
            <InfoValue>
              {foreignCurrencyData.amount.toLocaleString()} 원
            </InfoValue>
          </InfoRow>
        </InfoSection>

        <ButtonGroup>
          <CancelButton onClick={onClose}>닫기</CancelButton>
          <ConfirmButton onClick={onConfirm}>발행 취소</ConfirmButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
}

export default FXReceiptCancelModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  width: 480px;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.12);
`;

const Title = styled.h2`
  color: var(--Mono-100, #030303);

  /* WebHead/21_BD */
  font-family: Pretendard, sans-serif;
  font-size: 21px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 31.5px */
  letter-spacing: -0.42px;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: var(--Mono-65, #5f6165);

  /* WebBody/14_MD */
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
  margin-bottom: 16px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  border-radius: 8px;
  background: var(--Mono-05, #f5f6f7);
  margin-bottom: 24px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoLabel = styled.span`
  color: var(--Mono-65, #5f6165);
  /* WebBody/12_MD */
  font-family: Pretendard, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 18px */
  letter-spacing: -0.24px;
`;

const InfoValue = styled.span`
  color: var(--Mono-65, #5f6165);
  /* WebBody/12_MD */
  font-family: Pretendard, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.24px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const CancelButton = styled.button`
  flex: 1;
  height: 52px;
  border-radius: 8px;
  background: var(--Mono-10, #e5e6e8);
  color: var(--Mono-80, #3a3b3e);
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 24px */
  cursor: pointer;

  &:hover {
    background: var(--Mono-05, #f5f6f7);
  }
`;

const ConfirmButton = styled.button`
  flex: 1;
  height: 52px;
  border-radius: 8px;
  border: none;
  background: #246cf6;
  color: #fff;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;
